import type { SectionSvgUploadResource } from '../section.svg.upload/section.svg.upload.resource';
import type { ImageItem, Unit } from '../types';
import type { UiNotificationService } from '../ui/notification';
import type { SvgSectionResource } from './svg.section.resource';

import angular from 'angular';

import { logger } from '../services/logger';
import { getPathKeyPoints } from './getPathKeyPoints';

interface SectionSvgUploadCtrlProps {
  sectionId: string;
  houseId?: string;
  $el: JQLite;
  loading: boolean;
  units: angular.resource.IResource<{ [key: string]: Unit }>;
  svgUrl?: string | null;
  imageUrl?: string | null;
  getSvgUrl: () => string | void;
  initSvg: () => void;
  /**
   * whether to init in debug mode
   */
  notifyErrors: boolean;
}

svgSectionCtrl.$inject = [
  '$scope',
  'SvgSection',
  'SectionSvgUpload',
  '$compile',
  '$http',
  '$notification',
];

export function svgSectionCtrl(
  this: SectionSvgUploadCtrlProps,
  $scope: angular.IScope,
  SvgSection: SvgSectionResource,
  SectionSvgUpload: SectionSvgUploadResource,
  $compile: angular.ICompileService,
  $http: angular.IHttpService,
  $notification: UiNotificationService,
) {
  const vm = this;

  vm.loading = true;

  let imagesPromise: Promise<{
    finalImage: string | null;
    finalSvg: string | null;
  }> = Promise.resolve({
    finalImage: vm.imageUrl || null,
    finalSvg: vm.svgUrl || null,
  });

  if (vm.houseId) {
    const houseReq = SvgSection.getHouseUnitsBySection({ id: vm.houseId });
    let resolveImages: (params: {
      finalImage: string | null;
      finalSvg: string | null;
    }) => void = () => {};
    imagesPromise = new Promise((resolve) => {
      resolveImages = resolve;
    });

    // @ts-expect-error
    vm.units = {
      $resolved: false,
      $promise: houseReq.$promise.then((resp) => {
        if (!resp.success) {
          throw Object.assign(
            new Error('Unexpected from getHouseUnitsBySection'),
            {
              resp,
            },
          );
        }

        const section = resp.sections[vm.sectionId];
        const units = Object.fromEntries(
          section.units.map((unit) => {
            vm.units[unit.unit_id] = unit;

            return [unit.unit_id, unit];
          }),
        );

        vm.units.$resolved = true;

        const resolveImage = (images: ImageItem[]): string | null => {
          if (!images.length) {
            return null;
          }

          return images[images.length - 1].url.replace(/^http:/, 'https:');
        };

        resolveImages({
          finalImage: vm.imageUrl || resolveImage(section.images.bitmap),
          finalSvg: vm.svgUrl || resolveImage(section.images.svg),
        });

        return units;
      }),
    };
  } else {
    vm.units = SvgSection.getUnits({ id: vm.sectionId });

    if (!vm.svgUrl || !vm.imageUrl) {
      imagesPromise = SectionSvgUpload.fetchImages({
        sectionId: vm.sectionId,
      });
    }
  }

  vm.units.$promise.catch((error) => {
    logger.error('Error fetching units', {
      error,
      sectionId: vm.sectionId,
    });
  });

  imagesPromise
    .then(async ({ finalSvg, finalImage }) => {
      if (!finalSvg || !finalImage) {
        // this is a hack to show units grid in case when svg navigation is not available
        // see _svg-section.scss
        vm.$el.removeClass('svg-section--available');

        return;
      }

      const { data: svgCode } = await $http.get<string>(finalSvg);
      const units = await vm.units.$promise;

      const $svg = buildSvg({
        code: svgCode,
        units,
        imageUrl: finalImage,
        onError(message) {
          if (vm.notifyErrors) {
            $notification.show({
              type: 'error',
              text: message,
            });
          }
        },
      });

      // wait when image will be preloaded
      await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = reject;
        image.src = finalImage;
      });

      const html = $compile($svg)($scope);
      vm.$el.append(html);
    })
    .catch((error) => {
      logger.error('Error loading section navi images', {
        error,
        sectionId: vm.sectionId,
      });

      // fall back to the units grid, same as when svg navigation is not available
      vm.$el.removeClass('svg-section--available');
    })
    .finally(() => {
      $scope.$apply(() => {
        vm.loading = false;
      });
    });
}

/**
 * Sets image to display in svg and replaces <rect>, <polygon>, <text> with
 * <foreignObject> to display interactive angularjs content
 */
function buildSvg({
  code,
  imageUrl,
  units,
  onError,
}: {
  code: string;
  imageUrl: string;
  units: { [key: string]: Unit };
  onError: (message: string) => void;
}): JQuery {
  const $svg = angular.element(code);
  // eslint-disable-next-line prefer-destructuring
  const imageEl = $svg.find('image')[0];

  if (!imageEl) {
    throw new Error('Can not find image element in svg');
  }

  // replace xlink:href with correct image src
  imageEl.setAttribute('xlink:href', imageUrl);

  const unitMap = new Map(
    Object.values(units).map((unit) => [unit.num, unit.unit_id]),
  );

  const formatNumber = (input: number | string): number =>
    Number(Number(input).toFixed(4));

  // replace <rect> with <foreignObject> to display HTML content with angular directives
  $svg.find('rect').each((index, rect: SVGRectElement) => {
    // for rect elements just copy their cords on the <foreignObject>
    const $rect = angular.element(rect);

    try {
      const { $unit } = createUnitEl($rect);
      const $foreignObject = angular
        .element(
          document.createElementNS(
            'http://www.w3.org/2000/svg',
            'foreignObject',
          ),
        )
        .append($unit);

      ['x', 'y', 'width', 'height'].forEach((attr) => {
        if (rect.hasAttribute(attr)) {
          $foreignObject.attr(attr, rect.getAttribute(attr));
        }
      });

      $rect.replaceWith($foreignObject);
    } catch (error) {
      $rect.remove();

      logger.error('Error processing <rect> element', {
        error,
      });

      onError(`Error processing ${rect.outerHTML} element: ${error.message}`);
    }
  });

  // replace <polygon> with <foreignObject> to display HTML content with angular directives
  $svg.find('polygon').each((index, polygon: SVGPolygonElement) => {
    // for polygon elements we need calculate square cords for <foreignObject>
    // so that polygon will fit inside
    // then we need to create clipMask to apply the shape of <polygon>
    // on the <foreignObject>
    const $polygon = angular.element(polygon);

    try {
      maskFromPoints({
        $el: $polygon,
        points: Array.from(polygon.points),
        index,
      });
    } catch (error) {
      $polygon.remove();

      logger.error('Error processing <polygon> element', {
        error,
      });

      onError(
        `Error processing ${polygon.outerHTML} element: ${error.message}`,
      );
    }
  });

  // replace <path> with <foreignObject> to display HTML content with angular directives
  $svg.find('path').each((index, path: SVGPathElement) => {
    // for path elements we need calculate square cords for <foreignObject>
    // so that path will fit inside
    // then we need to create clipMask to apply the shape of <path>
    // on the <foreignObject>
    const $path = angular.element(path);

    try {
      const pathData = path.getAttribute('d');

      if (!pathData) {
        throw new Error(
          `Expected path to have d attribute. Got: ${path.outerHTML}`,
        );
      }

      if (!/z/i.test(pathData)) {
        throw new Error(
          `Expected closed path (ends with Z). Got: ${path.outerHTML}`,
        );
      }

      const points = getPathKeyPoints(pathData);

      maskFromPoints({
        $el: $path,
        points,
        index,
      });
    } catch (error) {
      $path.remove();

      logger.error('Error processing <path> element', {
        error,
      });

      onError(`Error processing ${path.outerHTML} element: ${error.message}`);
    }
  });

  /**
   * Searches for the next <text> element relative to `$el`,
   * parses the number from this element, resolves unitId by this number
   * and returns new element with `svg-unit` directive configured with that unitId
   */
  function createUnitEl($el: JQuery): { $unit: JQuery; unitId: string } {
    const $text = $el.next('text');

    if (!$text[0]) {
      throw new Error(`Can not find unit number for rect: ${$el[0].outerHTML}`);
    }

    const unitNumber = String($text.text()).trim();

    $text.remove();

    const unitId = unitMap.get(unitNumber);

    if (!unitId) {
      throw new Error(`Can not map unit number ${unitNumber} to unit id`);
    }

    return {
      unitId,
      $unit: angular
        .element('<div>')
        .css({ height: '100%' })
        .attr({
          xmlns: 'http://www.w3.org/1999/xhtml',
          'svg-unit': '',
          unit: `vm.units[${unitId}]`,
          'public-mode': 'vm.publicMode',
        }),
    };
  }

  function maskFromPoints({
    $el,
    points,
    index,
  }: {
    $el: JQuery;
    points: { x: number; y: number }[];
    index: number;
  }) {
    const { unitId, $unit } = createUnitEl($el);
    const clipId = `clipPathUnit-${index}-${unitId}`;

    let minX = Infinity;
    let maxX = 0;
    let minY = Infinity;
    let maxY = 0;

    for (const point of points) {
      minX = Math.min(minX, formatNumber(point.x));
      maxX = Math.max(maxX, formatNumber(point.x));
      minY = Math.min(minY, formatNumber(point.y));
      maxY = Math.max(maxY, formatNumber(point.y));
    }

    const width = formatNumber(maxX - minX);
    const height = formatNumber(maxY - minY);

    const $foreignObject = angular
      .element(
        document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject'),
      )
      .attr({
        // x: minX,
        // y: minY,
        width,
        height,
        'clip-path': `url(#${clipId})`,
      })
      .css({
        // can not use x, y attributes because of a bug in chrome:
        // https://stackoverflow.com/a/69586234/2039203
        transform: `translate(${minX}px, ${minY}px)`,
      })
      .append($unit);

    const mask = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'polygon',
    );
    mask.setAttribute(
      'points',
      points.map((point) => `${point.x},${point.y}`).join(' '),
    );

    const $defs = angular
      .element(document.createElementNS('http://www.w3.org/2000/svg', 'defs'))
      .append(
        angular
          .element(
            document.createElementNS('http://www.w3.org/2000/svg', 'clipPath'),
          )
          .css({
            // compensate for transform of foreignObject
            // https://stackoverflow.com/a/69586234/2039203
            transform: `translate(-${minX}px, -${minY}px)`,
          })
          .attr('id', clipId)
          .append(mask),
      );

    $el.replaceWith([$defs, $foreignObject]);
  }

  return $svg;
}
